import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { DateTime, Duration, Interval } from "luxon";
import { Ranger, useRanger } from "@tanstack/react-ranger";
import {
  alpha,
  SliderPropsColorOverrides,
  styled,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import { SliderHandle } from "./SliderHandle";
import { OverridableStringUnion } from "@mui/types";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type,@typescript-eslint/no-empty-interface
interface IntervalSliderOwnerState extends Exclude<IntervalSliderProps, "sx"> {
  // …key value pairs for the internal state that you want to style the slot
  // but don't want to expose to the users
}

const IntervalSliderRoot = styled("div", {
  name: "IntervalSliderRoot",
  slot: "root",
})<{ ownerState: IntervalSliderOwnerState }>(({ theme }) => ({
  position: "relative",
  height: 52,
}));

const IntervalSliderRail = styled("div", {
  name: "IntervalSliderRail",
  slot: "rail",
})<{ ownerState: IntervalSliderOwnerState }>(({ theme }) => ({
  position: "relative",
  top: "50%",
  userSelect: "none",
  height: "4px",
  background: "#ddd",
  boxShadow: "inset 0 1px 2px rgba(0,0,0,.6)",
  borderRadius: "2px",
}));

const IntervalSliderTick = styled("div", {
  name: "IntervalSliderTick",
  slot: "tick",
})<{ percentage: number }>(({ theme, percentage }) => ({
  position: "absolute",
  top: "5px",
  left: `${percentage}%`,
  transform: "translateX(-50%)",
  ":before": {
    content: '""',
    position: "absolute",
    left: 0,
    background: "rgba(0, 0, 0, 0.2)",
    height: "5px",
    width: "2px",
    transform: "translate(-50%, 0.7rem)",
  },
}));

const IntervalSliderTickLabel = styled("div", {
  name: "IntervalSliderTickLabel",
  slot: "tickLabel",
})(({ theme }) => ({
  position: "absolute",
  fontSize: "0.6rem",
  color: "rgba(0, 0, 0, 0.5)",
  top: "100%",
  transform: "translate(-50%, 1.2rem)",
  whiteSpace: "nowrap",
}));

const IntervalSliderMarkerDot = styled("div", {
  name: "IntervalSliderMarkerDot",
  slot: "markerDot",
})<{ percentage: number }>(({ theme, percentage }) => ({
  position: "absolute",
  top: "10px",
  left: `${percentage}%`,
  transform: "translateX(-50%)",
  width: 4,
  height: 4,
  minWidth: 1,
  borderRadius: 999,
  background: alpha(theme.palette.text.primary, 0.25),
  pointerEvents: "none",
}));

const IntervalSliderMarkerRange = styled("div", {
  name: "IntervalSliderMarkerRange",
  slot: "markerRange",
})<{ left: number; width: number }>(({ theme, left, width }) => ({
  position: "absolute",
  top: "10px",
  left: `${left}%`,
  width: `${width}%`,
  minWidth: 1,
  height: 4,
  borderRadius: 999,
  background: alpha(theme.palette.text.primary, 0.18),
  pointerEvents: "none",
}));

export type SegmentColors = Array<string | undefined>;

export type IntervalSliderMarker =
  | {
      at: DateTime;
      id?: string;
    }
  | {
      interval: Interval<true>;
      id?: string;
    };

const IntervalSliderSegment = styled("div", {
  name: "IntervalSliderSegment",
  slot: "segment",
})<{
  index: number;
  left: number;
  width: number;
  segmentColors: SegmentColors;
}>(({ theme, index, left, width, segmentColors }) => ({
  position: "absolute",
  background: segmentColors[index],
  left: `${left}%`,
  width: `${width}%`,
  height: index === 1 ? "14px" : "100%",
  top: index === 1 ? "-5px" : "0",
}));

const IntervalSliderPlate = styled("div", {
  name: "IntervalSliderPlate",
  slot: "plate",
})<{
  left: number;
  width: number;
  color: string;
  dragging: boolean;
}>(({ theme, left, width, color, dragging }) => ({
  position: "absolute",
  top: "-5px",
  left: `${left}%`,
  width: `${width}%`,
  height: "14px",
  cursor: dragging ? "grabbing" : "grab",
  // Slightly visible overlay plate inside the selected segment
  background: alpha(color, 0.08),
  /*outline: `1px dashed ${alpha(color, 0.3)}`,*/
  borderRadius: 2,
  // Appear only when hovered to reduce visual noise
  opacity: dragging ? 1 : 0,
  transition: "opacity 120ms ease",
  "&:hover": {
    opacity: 1,
  },
}));

export interface IntervalSliderProps {
  maxInterval: Interval<true>;
  selectedInterval: Interval<true>;
  onSelectedIntervalChange: (interval: Interval) => void;
  markers?: IntervalSliderMarker[];
  /**
   * The maximum duration allowed for the selected interval.
   * When set, limits how far apart the user can drag each handle.
   */
  maxSelectionDuration?: Duration;
  /**
   * The color of the component.
   * It supports both default and custom theme colors, which can be added as shown in the
   * [palette customization guide](https://mui.com/material-ui/customization/palette/#custom-colors).
   * @default 'primary'
   */
  color?: OverridableStringUnion<
    "primary" | "secondary" | "error" | "info" | "success" | "warning",
    SliderPropsColorOverrides
  >;
  sx?: SxProps;
}

export const IntervalSlider = memo<IntervalSliderProps>(function IntervalSlider(props) {
  const {
    maxInterval,
    selectedInterval,
    onSelectedIntervalChange,
    markers,
    maxSelectionDuration,
  } = props;
  const theme = useTheme();
  const color = props.color ?? theme.palette.primary.dark;
  const activeSegmentTextColor = theme.palette.primary.contrastText;
  const segmentSelectedAreaColor = alpha(color, 0.9);
  const segmentColors: SegmentColors = useMemo(
    () => [undefined, segmentSelectedAreaColor, undefined],
    [segmentSelectedAreaColor]
  );

  const start = maxInterval.start.startOf("day");
  const end = maxInterval.end.startOf("day");

  const min = 0;
  const totalDays = Math.max(1, end.diff(start, "days").days);

  const sliderValue = useMemo<[number, number]>(() => {
    return [
      Math.round(
        selectedInterval.start.startOf("day").diff(start, "days").days
      ),
      Math.round(selectedInterval.end.startOf("day").diff(start, "days").days),
    ];
  }, [selectedInterval, start]);

  const rangerRef = React.useRef<HTMLDivElement>(null);
  const isDraggingPlateRef = React.useRef(false);
  const dragStartClientXRef = React.useRef<number | null>(null);
  const dragStartValuesRef = React.useRef<[number, number] | null>(null);
  // Track the last computed values while dragging the plate, to emit once on release
  const plateLastComputedNextRef = React.useRef<[number, number] | null>(null);
  // Track the last released values so we can keep the UI stable until the controlled props catch up.
  const plateLastReleasedValuesRef = React.useRef<[number, number] | null>(
    null
  );
  // Visual (optimistic) values while dragging the plate so UI updates during drag
  const [visualValues, setVisualValues] = useState<[number, number] | null>(
    null
  );
  const [
    isAwaitingPropSyncAfterPlateRelease,
    setIsAwaitingPropSyncAfterPlateRelease,
  ] = useState(false);
  const effectiveValues: [number, number] = visualValues ?? sliderValue;

  useEffect(() => {
    if (!isAwaitingPropSyncAfterPlateRelease) return;

    const target = plateLastReleasedValuesRef.current;
    if (!target) {
      setVisualValues(null);
      setIsAwaitingPropSyncAfterPlateRelease(false);
      return;
    }

    // If parent-controlled props have caught up, drop the optimistic values.
    if (sliderValue[0] === target[0] && sliderValue[1] === target[1]) {
      setVisualValues(null);
      setIsAwaitingPropSyncAfterPlateRelease(false);
      plateLastReleasedValuesRef.current = null;
      return;
    }

    // Storybook `updateArgs` can be async; if props never update (parent rejects change),
    // fall back to controlled values after a short grace period.
    const timeoutId = window.setTimeout(() => {
      setVisualValues(null);
      setIsAwaitingPropSyncAfterPlateRelease(false);
      plateLastReleasedValuesRef.current = null;
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [isAwaitingPropSyncAfterPlateRelease, sliderValue]);

  const fromSliderValue = useCallback(
    ([startDay, endDay]: readonly number[]): Interval =>
      Interval.fromDateTimes(
        start.plus({ days: startDay }),
        start.plus({ days: endDay })
      ),
    [start]
  );

  // Convert maxSelectionDuration to days for constraint checking
  const maxSelectionDays = useMemo(() => {
    if (!maxSelectionDuration) return null;
    return Math.floor(maxSelectionDuration.as("days"));
  }, [maxSelectionDuration]);

  // Handle drag event - fires continuously while dragging handles
  const handleRangerDrag = useCallback(
    (instance: Ranger<HTMLDivElement>) => {
      let [newStart, newEnd] = instance.sortedValues;

      // Apply maxSelectionDuration constraint if set
      if (maxSelectionDays != null) {
        const currentDuration = newEnd - newStart;
        if (currentDuration > maxSelectionDays) {
          const prevStart = sliderValue[0];
          const prevEnd = sliderValue[1];

          if (newStart !== prevStart) {
            newEnd = newStart + maxSelectionDays;
            if (newEnd > totalDays) {
              newEnd = totalDays;
              newStart = totalDays - maxSelectionDays;
            }
          } else {
            newStart = newEnd - maxSelectionDays;
            if (newStart < min) {
              newStart = min;
              newEnd = min + maxSelectionDays;
            }
          }
        }
      }

      // Update visual values during drag so duration display updates in real-time
      setVisualValues([newStart, newEnd]);
    },
    [maxSelectionDays, sliderValue, totalDays, min]
  );

  const handleRangerChange = useCallback(
    (instance: Ranger<HTMLDivElement>) => {
      console.log("handleRangerChange: ", instance.sortedValues);
      let [newStart, newEnd] = instance.sortedValues;

      // Apply maxSelectionDuration constraint if set
      if (maxSelectionDays != null) {
        const currentDuration = newEnd - newStart;
        if (currentDuration > maxSelectionDays) {
          // Determine which handle moved by comparing with previous values
          const prevStart = sliderValue[0];
          const prevEnd = sliderValue[1];

          if (newStart !== prevStart) {
            // Start handle moved - constrain end to maintain max duration
            newEnd = newStart + maxSelectionDays;
            // Ensure we don't exceed the max boundary
            if (newEnd > totalDays) {
              newEnd = totalDays;
              newStart = totalDays - maxSelectionDays;
            }
          } else {
            // End handle moved - constrain start to maintain max duration
            newStart = newEnd - maxSelectionDays;
            // Ensure we don't go below the min boundary
            if (newStart < min) {
              newStart = min;
              newEnd = min + maxSelectionDays;
            }
          }
        }
      }

      // Clear visual values when drag ends - controlled props will take over
      setVisualValues(null);

      const changedInterval = fromSliderValue([newStart, newEnd]);
      onSelectedIntervalChange(changedInterval);
    },
    [fromSliderValue, onSelectedIntervalChange, maxSelectionDays, sliderValue, totalDays, min]
  );

  const rangerInstance = useRanger<HTMLDivElement>({
    getRangerElement: () => rangerRef.current,
    values: effectiveValues,
    min: min,
    max: totalDays,
    stepSize: 1,
    tickSize: 70,
    onDrag: handleRangerDrag,
    onChange: handleRangerChange,
  });

  const handleValueFormatter = useCallback(
    (value: number): string => {
      const date = start.plus({ days: value });

      return date.toLocaleString(DateTime.DATE_SHORT);
    },
    [start]
  );

  // Derived plate geometry using current effective slider values
  const leftPercent = rangerInstance.getPercentageForValue(effectiveValues[0]);
  const rightPercent = rangerInstance.getPercentageForValue(effectiveValues[1]);
  const plateLeft = Math.min(leftPercent, rightPercent);
  const plateWidth = Math.max(0, Math.abs(rightPercent - leftPercent));
  const intervalDays = Math.max(0, effectiveValues[1] - effectiveValues[0]);

  const clamp = (val: number, minVal: number, maxVal: number) =>
    Math.min(Math.max(val, minVal), maxVal);

  const markerNodes = useMemo(() => {
    if (!markers || markers.length === 0) return null;

    const toDayIndex = (dt: DateTime) =>
      Math.round(dt.startOf("day").diff(start, "days").days);

    return markers.map((marker, idx) => {
      if ("at" in marker) {
        const day = toDayIndex(marker.at);
        if (day < min || day > totalDays) return null;
        const percentage = rangerInstance.getPercentageForValue(day);
        return (
          <IntervalSliderMarkerDot
            key={marker.id ?? `point-${idx}`}
            data-testid={
              marker.id
                ? `IntervalSlider__marker-point-${marker.id}`
                : undefined
            }
            percentage={percentage}
          />
        );
      }

      const markerStart = toDayIndex(marker.interval.start);
      const markerEnd = toDayIndex(marker.interval.end);

      const unclampedStart = Math.min(markerStart, markerEnd);
      const unclampedEnd = Math.max(markerStart, markerEnd);
      if (unclampedEnd < min || unclampedStart > totalDays) return null;

      const clampedStart = clamp(unclampedStart, min, totalDays);
      const clampedEnd = clamp(unclampedEnd, min, totalDays);
      const leftPercent = rangerInstance.getPercentageForValue(clampedStart);
      const rightPercent = rangerInstance.getPercentageForValue(clampedEnd);

      const left = Math.min(leftPercent, rightPercent);
      const width = Math.max(0, Math.abs(rightPercent - leftPercent));
      // Width can become sub-pixel (or 0 due to rounding) for very long `maxInterval`s.
      // Still render and rely on CSS `min-width: 1px` so the marker remains visible.
      if (!Number.isFinite(width)) return null;

      return (
        <IntervalSliderMarkerRange
          key={marker.id ?? `range-${idx}`}
          data-testid={
            marker.id ? `IntervalSlider__marker-range-${marker.id}` : undefined
          }
          left={left}
          width={width}
        />
      );
    });
  }, [clamp, markers, min, rangerInstance, start, totalDays]);

  const handlePlatePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!rangerRef.current) return;
    // Prevent starting a drag on the rail/handles
    e.stopPropagation();
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);

    // Cancel any pending "wait for props" period from a previous release.
    setIsAwaitingPropSyncAfterPlateRelease(false);
    plateLastReleasedValuesRef.current = null;

    isDraggingPlateRef.current = true;
    dragStartClientXRef.current = e.clientX;
    dragStartValuesRef.current = [...sliderValue];
    // start visual drag from current slider values
    setVisualValues([...sliderValue]);
  };

  const handlePlatePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingPlateRef.current || !rangerRef.current) return;
    const rect = rangerRef.current.getBoundingClientRect();
    const widthPx = rect.width || 1;
    const deltaPx = (dragStartClientXRef.current ?? e.clientX) - e.clientX;
    // Negative sign above means moving right increases values
    const deltaPxCorrected = -deltaPx;
    const daysPerPx = totalDays / widthPx;
    const rawDeltaDays = deltaPxCorrected * daysPerPx;
    const deltaDays = Math.round(rawDeltaDays);

    const original = dragStartValuesRef.current ?? sliderValue;
    const duration = Math.max(0, original[1] - original[0]);
    let nextStart = original[0] + deltaDays;
    nextStart = clamp(nextStart, min, Math.max(min, totalDays - duration));
    const nextEnd = nextStart + duration;

    // While dragging, update visuals only and store the last computed values for final emit
    plateLastComputedNextRef.current = [nextStart, nextEnd];
    setVisualValues([nextStart, nextEnd]);
  };

  const handlePlatePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingPlateRef.current) return;
    e.stopPropagation();
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {
      // Ignore
    }
    // Emit the final value once on release, mirroring SliderHandle behavior
    const finalValues = plateLastComputedNextRef.current;
    if (
      finalValues &&
      (finalValues[0] !== sliderValue[0] || finalValues[1] !== sliderValue[1])
    ) {
      onSelectedIntervalChange(fromSliderValue(finalValues));
    }

    // Keep the last visual position until the controlled `selectedInterval` updates.
    // This avoids a one-render "jump back" when the parent update is async (e.g. Storybook args).
    if (finalValues) {
      plateLastReleasedValuesRef.current = finalValues;
      setVisualValues(finalValues);
      setIsAwaitingPropSyncAfterPlateRelease(true);
    } else {
      setVisualValues(null);
      setIsAwaitingPropSyncAfterPlateRelease(false);
      plateLastReleasedValuesRef.current = null;
    }

    isDraggingPlateRef.current = false;
    dragStartClientXRef.current = null;
    dragStartValuesRef.current = null;
    plateLastComputedNextRef.current = null;
  };

  return (
    <IntervalSliderRoot ownerState={props} sx={props.sx}>
      <IntervalSliderRail ownerState={props} ref={rangerRef}>
        {rangerInstance.getTicks().map(({ value, key, percentage }) => {
          const date = start.plus({ days: value });
          const dateText = date.toLocaleString(DateTime.DATE_SHORT);
          /*console.log(
            `IntervalSlider: tick: ${value} (${percentage}%) -> ${date.toISO()}`
          );*/
          return (
            <IntervalSliderTick key={key} percentage={percentage}>
              <IntervalSliderTickLabel>{dateText}</IntervalSliderTickLabel>
            </IntervalSliderTick>
          );
        })}
        {rangerInstance.getSteps().map(({ left, width }, i) => {
          return (
            <IntervalSliderSegment
              key={i}
              index={i}
              left={left}
              width={width}
              segmentColors={segmentColors}
            >
              {i === 1 && (
                <Typography
                  variant={"caption"}
                  align={"center"}
                  color={activeSegmentTextColor}
                  noWrap
                  sx={{
                    position: "absolute",
                    top: "-0.5px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: "0.6rem",
                  }}
                >
                  {(visualValues?.[1] ?? effectiveValues[1]) -
                    (visualValues?.[0] ?? effectiveValues[0])}{" "}
                  d
                </Typography>
              )}
            </IntervalSliderSegment>
          );
        })}
        {markerNodes}
        {/* Drag plate to move the whole interval while keeping duration */}
        {plateWidth > 0 ? (
          <IntervalSliderPlate
            left={plateLeft}
            width={plateWidth}
            color={theme.palette.primary.main}
            dragging={isDraggingPlateRef.current}
            onPointerDown={handlePlatePointerDown}
            onPointerMove={handlePlatePointerMove}
            onPointerUp={handlePlatePointerUp}
          ></IntervalSliderPlate>
        ) : null}
        {rangerInstance.handles().map((handleProps, i) => (
          <SliderHandle
            key={i}
            ranger={rangerInstance}
            {...handleProps}
            valueFormatter={handleValueFormatter}
          />
        ))}
      </IntervalSliderRail>
    </IntervalSliderRoot>
  );
});
